import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Hero from './components/Hero/Hero';
import About from './components/About/About';
import Portfolio from './components/Portfolio/Portfolio';
import Prices from './components/Prices/Prices';
import Contact from './components/Contact/Contact';
import Shop from './components/Shop/Shop';
import GlobalStyle from './styles/GlobalStyle';
import { CartProvider } from './context/CartContext';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import DownloadPage from './components/Download/Download';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Dashboard from './components/Dashboard/Dashboard';
import AnalyticsAdmin from './components/Analytics/AnalyticsAdmin';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminAnalyticsRoute from './components/Auth/AdminAnalyticsRoute';
import AnalyticsBootstrap from './components/Analytics/AnalyticsBootstrap';
import { API_BASE } from './config/api';

function AnimatedRoutes() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Hero />} />
        <Route path="/about" element={<About />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/prices" element={<Prices />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/analytics-setup" element={<AnalyticsBootstrap />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/dashboard/analytics"
          element={
            <ProtectedRoute>
              <AdminAnalyticsRoute>
                <AnalyticsAdmin />
              </AdminAnalyticsRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/analytics/settings"
          element={
            <ProtectedRoute>
              <AdminAnalyticsRoute>
                <AnalyticsAdmin view="settings" />
              </AdminAnalyticsRoute>
            </ProtectedRoute>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/paypal-config`);
        const config = await response.json();
        setPaypalConfig(config);
      } catch (error) {
        console.error('Failed to fetch PayPal config:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        backgroundColor: '#0a0b0d',
        color: '#edeef0',
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif"
      }}>
        Loading...
      </div>
    );
  }

  if (!paypalConfig) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        backgroundColor: '#0a0b0d',
        color: '#f87171',
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif"
      }}>
        Failed to load PayPal configuration. Please refresh the page.
      </div>
    );
  }

  return (
    <AuthProvider>
      <PayPalScriptProvider options={{ 
        "client-id": paypalConfig.clientId,
        currency: "USD",
        components: "buttons",
        intent: "capture"
      }}>
        <CartProvider>
          <Router>
            <GlobalStyle />
            <Header />
            <AnimatedRoutes />
            <Footer />
          </Router>
        </CartProvider>
      </PayPalScriptProvider>
    </AuthProvider>
  );
}

export default App;